import { useCallback, useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/menu/main-menu';
import { DAY_PART_SELECTIONS } from 'state/menu/main-menu/types';
import { ServiceMode } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import logger from 'utils/logger';
import {
  IRbiRestaurant,
  getMinutesUntilRestaurantClose,
  useGetRestaurantFn,
} from 'utils/restaurant';
import { routes } from 'utils/routing';
import { isPickup } from 'utils/service-mode/service-mode';

import { ISmartAlert, SmartAlert } from './types';

const MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS = 30;
const MINUTES_BEFORE_RESTAURANT_CLOSE = 30;
const MINUTES_BEFORE_PICK_UP_WINDOW_ENDS = 10;
const SMART_ALERT_REFRESH_INTERVAL = 60 * 1000;

type UseSmartAlertProps = {
  serverOrderServiceMode?: ServiceMode;
  serverOrderStoreId?: string;
};

export const useSmartAlert = (props?: UseSmartAlertProps): ISmartAlert => {
  const { serverOrderServiceMode, serverOrderStoreId } = props || {};
  const isSmartAlertsEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMART_ALERTS);
  const { formatMessage } = useIntl();
  const [tsmSmartAlert, setTsmSmartAlert] = useState<SmartAlert | null>(null);
  const [orderSmartAlert, setOrderSmartAlert] = useState<SmartAlert | null>(null);
  const [orderRestaurant, setOrderRestaurant] = useState<IRbiRestaurant | null>(null);

  const { store: restaurant } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();

  const getRestaurant = useGetRestaurantFn();

  const fetchOrderRestaurant = useCallback(
    async (serverOrderStoreId: string) => {
      try {
        const orderRestaurant = await getRestaurant(serverOrderStoreId);
        setOrderRestaurant(orderRestaurant || null);
      } catch (error) {
        logger.error({ error, message: 'Failed to fetch server order restaurant' });
        setOrderRestaurant(null);
      }
    },
    [getRestaurant]
  );

  const breakfastInterval = activeDayParts.find(
    item => item?.key === DAY_PART_SELECTIONS.BREAKFAST_KEY
  );

  const generateOrderAlert = useCallback(
    (serverOrderServiceMode: ServiceMode, orderRestaurant: IRestaurant) => {
      // Pick Up window ends
      const isPickupServiceMode = isPickup(serverOrderServiceMode);

      const minutesForClosing = getMinutesUntilRestaurantClose({
        restaurant: orderRestaurant,
        serviceMode: serverOrderServiceMode,
      });

      if (
        isPickupServiceMode &&
        minutesForClosing > 0 &&
        minutesForClosing <= MINUTES_BEFORE_PICK_UP_WINDOW_ENDS
      ) {
        setOrderSmartAlert({
          message: formatMessage({ id: 'pickupWindow' }, { minutes: minutesForClosing }),
        });

        return;
      }

      setOrderSmartAlert(null);
    },
    [formatMessage]
  );

  const generateTsmAlert = useCallback(
    (remainingMinutesToBreakfastDayPartChange: number) => {
      // Breakfast DayPart ends
      if (
        remainingMinutesToBreakfastDayPartChange > 0 &&
        remainingMinutesToBreakfastDayPartChange <= MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS
      ) {
        setTsmSmartAlert({
          message: formatMessage(
            { id: 'breakfastDayPartEnds' },
            { minutes: remainingMinutesToBreakfastDayPartChange }
          ),
          linkUrl: routes.storeLocator,
        });

        return;
      }

      // Restaurant closing time
      const minutesForClosing = getMinutesUntilRestaurantClose({
        restaurant,
        serviceMode: ServiceMode.EAT_IN,
      });

      if (minutesForClosing > 0 && minutesForClosing <= MINUTES_BEFORE_RESTAURANT_CLOSE) {
        setTsmSmartAlert({
          message: formatMessage({ id: 'restaurantCloses' }, { minutes: minutesForClosing }),
          linkUrl: routes.storeLocator,
        });

        return;
      }

      setTsmSmartAlert(null);
    },
    [formatMessage, restaurant]
  );

  const generateAlerts = useCallback(() => {
    // TODO: Replace `new Date()` with the store's current date based on its timezone,
    // once `activeDayParts` are also calculated using that same timezone.
    const remainingMinutesToBreakfastDayPartChange = breakfastInterval?.endTime
      ? differenceInMinutes(breakfastInterval?.endTime, new Date())
      : 0;

    if (serverOrderServiceMode && orderRestaurant) {
      // For the Order, we must use the restaurant where the order was placed, not the currently selected restaurant
      generateOrderAlert(serverOrderServiceMode, orderRestaurant as IRestaurant);
    }

    generateTsmAlert(remainingMinutesToBreakfastDayPartChange);
  }, [
    breakfastInterval?.endTime,
    generateOrderAlert,
    generateTsmAlert,
    orderRestaurant,
    serverOrderServiceMode,
  ]);

  useEffect(() => {
    if (!isSmartAlertsEnabled) {
      return;
    }

    const interval = setInterval(() => {
      generateAlerts();
    }, SMART_ALERT_REFRESH_INTERVAL);

    generateAlerts();

    return () => {
      clearInterval(interval);
    };
  }, [generateAlerts, isSmartAlertsEnabled]);

  useEffect(() => {
    serverOrderStoreId && fetchOrderRestaurant(serverOrderStoreId);
  }, [fetchOrderRestaurant, serverOrderStoreId]);

  return {
    tsmSmartAlert,
    orderSmartAlert,
  };
};
